import React from "react";
import { Cloud, CloudRain, CloudSnow, Sun, CloudSun, CloudDrizzle, CloudLightning, CloudFog, Wind } from "lucide-react";

// WMO weather code → icon + label (https://open-meteo.com/en/docs)
export function weatherIconFor(code) {
    if (code === 0) return { Icon: Sun, label: "Zonnig", color: "#C8922C" };
    if (code === 1) return { Icon: Sun, label: "Vooral zonnig", color: "#F59E0B" };
    if (code === 2) return { Icon: CloudSun, label: "Deels bewolkt", color: "#E5A93D" };
    if (code === 3) return { Icon: Cloud, label: "Bewolkt", color: "#9CA3AF" };
    if (code === 45 || code === 48) return { Icon: CloudFog, label: "Mistig", color: "#9CA3AF" };
    if (code >= 51 && code <= 57) return { Icon: CloudDrizzle, label: "Motregen", color: "#60A5FA" };
    if (code >= 61 && code <= 67) return { Icon: CloudRain, label: "Regen", color: "#3B82F6" };
    if (code >= 71 && code <= 77) return { Icon: CloudSnow, label: "Sneeuw", color: "#E0E7FF" };
    if (code >= 80 && code <= 82) return { Icon: CloudRain, label: "Regenbuien", color: "#3B82F6" };
    if (code === 85 || code === 86) return { Icon: CloudSnow, label: "Sneeuwbuien", color: "#E0E7FF" };
    if (code >= 95 && code <= 99) return { Icon: CloudLightning, label: "Onweer", color: "#7C3AED" };
    return { Icon: Cloud, label: "—", color: "#9CA3AF" };
}

const NL_WEEKDAYS = ["zo", "ma", "di", "wo", "do", "vr", "za"];

export default function WeatherStrip({ days = [], compact = false }) {
    if (!days || days.length === 0) return null;
    return (
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }} data-testid="weather-strip">
            {days.map((d) => {
                const { Icon, color, label } = weatherIconFor(d.weather_code);
                const dt = d.date ? new Date(d.date) : null;
                const day = dt ? NL_WEEKDAYS[dt.getDay()] : "";
                const dnum = dt ? dt.getDate() : "";
                const isRainy = (d.precipitation_chance ?? 0) >= 60 || (d.precipitation_mm ?? 0) >= 1;
                return (
                    <div
                        key={d.date}
                        title={`${label} · ${Math.round(d.temp_min)}/${Math.round(d.temp_max)}°C · ${Math.round(d.wind_kmh)} km/h wind${d.precipitation_mm ? ` · ${d.precipitation_mm}mm regen` : ""}`}
                        className={`bg-[#FFFFFF] border rounded-sm px-1.5 py-2 lg:px-2 lg:py-3 text-center transition ${isRainy ? "border-blue-500/20" : "border-zinc-100"}`}
                        data-testid={`weather-day-${d.date}`}
                    >
                        <div className="text-[9px] uppercase tracking-wider text-zinc-500 leading-none">{day}</div>
                        <div className="text-[11px] font-bold text-zinc-600 mt-0.5">{dnum}</div>
                        <Icon className="h-5 w-5 mx-auto my-1.5" style={{ color }} />
                        <div className="text-[11px] font-bold text-zinc-950">
                            {Math.round(d.temp_max)}°
                            <span className="text-zinc-500 font-normal">/{Math.round(d.temp_min)}°</span>
                        </div>
                        {!compact && (d.precipitation_chance != null) && (
                            <div className="text-[9px] text-blue-600 mt-1 flex items-center justify-center gap-0.5">
                                <CloudRain className="h-2.5 w-2.5" /> {Math.round(d.precipitation_chance)}%
                            </div>
                        )}
                        {!compact && (
                            <div className="text-[9px] text-zinc-500 mt-0.5 flex items-center justify-center gap-0.5">
                                <Wind className="h-2.5 w-2.5" /> {Math.round(d.wind_kmh)}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// Tiny inline weather pill for use inside an appointment card
export function WeatherPill({ day, className = "" }) {
    if (!day) return null;
    const { Icon, color } = weatherIconFor(day.weather_code);
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] text-zinc-600 ${className}`} title={`${Math.round(day.temp_min)}/${Math.round(day.temp_max)}°C · ${Math.round(day.wind_kmh)} km/h`}>
            <Icon className="h-3 w-3" style={{ color }} />
            <span className="font-bold">{Math.round(day.temp_max)}°</span>
            {(day.precipitation_chance ?? 0) >= 60 && (
                <span className="text-blue-600">{Math.round(day.precipitation_chance)}%</span>
            )}
        </span>
    );
}
